import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useChatContext } from "stream-chat-react";
import { useStaffChat } from "./StaffChatProvider";
import useOrgUsers from "./useOrgUsers";
import { createGroupChannelId, selectInModalProps } from "./chatUtils";

const GROUP_NAME_MAX_LENGTH = 60;

// Group chat: a name + people of the same organisation
const NewGroupModal = ({ show, onHide, onCreated }) => {
  const { client, currentUserId, team } = useStaffChat();
  const { setActiveChannel } = useChatContext();
  const { users, loading, error } = useOrgUsers(show);

  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (show) {
      setGroupName("");
      setSelectedMembers([]);
      setCreating(false);
    }
  }, [show]);

  const memberOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: user.name || user.id,
      })),
    [users]
  );

  const name = groupName.trim();
  const canCreate = name !== "" && selectedMembers.length > 0 && !creating;

  const createGroup = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      return;
    }
    setCreating(true);

    try {
      const channel = client.channel("messaging", createGroupChannelId(), {
        name: name,
        members: [
          currentUserId,
          ...selectedMembers.map((member) => member.value),
        ],
        team: team,
        is_group: true,
      });
      await channel.watch();

      setActiveChannel(channel);
      onHide();
      onCreated && onCreated(channel);
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Unable to create the group. Please try again.");
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={createGroup}>
        <Modal.Header closeButton>
          <Modal.Title
            style={{
              fontSize: "18px",
              fontFamily: "Arial, sans-serif",
              color: "#055837",
            }}
          >
            New group
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ fontFamily: "Arial, sans-serif" }}>
          <Form.Group className="mb-3">
            <Form.Label>
              Group name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Group name"
              value={groupName}
              maxLength={GROUP_NAME_MAX_LENGTH}
              onChange={(e) => setGroupName(e.target.value)}
              autoFocus
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Members <span className="text-danger">*</span>
            </Form.Label>
            <Select
              isMulti
              options={memberOptions}
              value={selectedMembers}
              onChange={(selected) => setSelectedMembers(selected || [])}
              isLoading={loading}
              isDisabled={creating}
              placeholder="Select staff"
              noOptionsMessage={() =>
                error ? error : loading ? "Loading staff..." : "No staff found."
              }
              closeMenuOnSelect={false}
              {...selectInModalProps}
            />
            {error && <div className="text-danger mt-2">{error}</div>}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="light"
            onClick={onHide}
            disabled={creating}
            className="w-50"
            style={{
              maxWidth: "130px",
              backgroundColor: "#dc3545",
              color: "white",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="light"
            disabled={!canCreate}
            className="w-50"
            style={{
              maxWidth: "130px",
              backgroundColor: "#055837",
              color: "#ffcc01",
            }}
          >
            {creating ? <Spinner animation="border" size="sm" /> : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewGroupModal;
